import React, { useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import {
  Badge,
  IconButton,
  Popover,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Typography,
  Box,
  Button,
  Divider,
  Chip,
  Menu,
  MenuItem,
  FormControl,
  Select,
  InputLabel
} from '@mui/material';
import {
  Notifications as NotificationsIcon,
  Announcement as AnnouncementIcon,
  Celebration as CelebrationIcon,
  Feedback as FeedbackIcon,
  Schedule as ScheduleIcon,
  Comment as CommentIcon,
  MonetizationOn as CoinsIcon,
  Message as MessageIcon,
  Person as PersonIcon,
  CheckCircle as CheckCircleIcon,
  MoreVert as MoreVertIcon,
  Clear as ClearIcon
} from '@mui/icons-material';
import { 
  selectNotifications, 
  selectUnreadCount, 
  markAsRead, 
  clearNotification, 
  markAllAsRead,
  fetchNotifications,
  fetchUnreadCount
} from '../../redux/notification/notification.actions';
import { NOTIFICATION_TYPES } from '../../redux/notification/notification.types';
import { destinoDaNotificacao } from '../../utils/destinoDaNotificacao';
import { rotuloParaAbrirOChamado } from '../../utils/avisoDeChatEncerrado';
import {
  lembretesParaAvisar,
  chatsEncerradosParaAvisar,
  avisosJaMostrados,
  anotarAvisosMostrados,
} from '../../utils/lembretesParaAvisar';
import { toast } from 'react-toastify';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/**
 * De quanto em quanto o sininho volta a perguntar à api.
 *
 * Um minuto para acompanhar a cadência real de quem cria notificação:
 * `agenda:dispatch-reminders` e `mural:publish-due` rodam `everyMinute()` no
 * agendador. Buscar mais rápido não faria o aviso chegar mais cedo.
 */
const INTERVALO_DO_SININHO_MS = 60000;

const NotificationBadge = () => {
  const dispatch = useDispatch();
  const history = useHistory();
  const notifications = useSelector(selectNotifications);
  const unreadCount = useSelector(selectUnreadCount);

  const [anchorEl, setAnchorEl] = useState(null);
  const [filter, setFilter] = useState('all');
  const [sortBy, setSortBy] = useState('newest');
  const [groupByTicket, setGroupByTicket] = useState(false);
  const [menuAnchorEl, setMenuAnchorEl] = useState(null);
  const [selectedNotification, setSelectedNotification] = useState(null);

  // Carrega as notificações, e volta a carregar de tempo em tempo.
  //
  // Antes era SÓ na montagem, e por isso nenhuma notificação chegava em
  // tempo real: o aviso só aparecia se a pessoa recarregasse a página. Era
  // isso que fazia o lembrete da agenda parecer não existir -- o servidor
  // sempre o criou (`agenda:dispatch-reminders`, a cada minuto), e ninguém
  // via.
  //
  // Polling e não socket porque o servidor de WebSocket não tem canal de
  // notificação nenhum: ele fala de sala e mensagem de chat. Abrir um canal
  // novo mexeria em três repos; o intervalo de um minuto já acompanha a
  // cadência real dos dois comandos que criam notificação
  // (`agenda:dispatch-reminders` e `mural:publish-due`, ambos
  // `everyMinute`).
  //
  // Pausa com a aba escondida: este componente vive no Template, montado o
  // tempo todo em todas as telas, e o operador deixa o sistema aberto o dia
  // inteiro. Sem a pausa seriam duas requisições por minuto por aba aberta,
  // para ninguém olhando.
  React.useEffect(() => {
    const buscar = () => {
      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') return;
      dispatch(fetchNotifications());
      dispatch(fetchUnreadCount());
    };

    buscar();
    const id = setInterval(buscar, INTERVALO_DO_SININHO_MS);

    // Ao voltar para a aba, busca na hora em vez de esperar o próximo
    // ciclo: quem volta ao sistema quer ver o estado de agora.
    const aoVoltar = () => {
      if (document.visibilityState === 'visible') buscar();
    };
    document.addEventListener('visibilitychange', aoVoltar);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltar);
    };
  }, [dispatch]);

  /**
   * Levanta UM aviso de lembrete e devolve o id dele.
   *
   * `autoClose: false` com `closeOnClick` e `draggable` desligados: o aviso sai
   * no X ou no "Ver o evento", e em mais lugar nenhum. Um lembrete que some
   * sozinho enquanto o operador está escrevendo para um cliente não lembra
   * nada -- pedido de 14/10/2026.
   *
   * O `toastId` é derivado do id da notificação, então o mesmo lembrete nunca
   * empilha dois avisos, mesmo que algo chame isto duas vezes.
   */
  const avisarLembrete = (lembrete) => {
    const idDoToast = `lembrete-${lembrete.id}`;

    toast.info(
      ({ closeToast }) => (
        <Box>
          {/* `data` primeiro, igual à lista -- ver a nota no aviso de chat
            * encerrado logo abaixo. */}
          <Typography variant="subtitle2" fontWeight={700}>
            {lembrete.data?.title || lembrete.title || 'Lembrete de compromisso'}
          </Typography>
          {(lembrete.data?.message || lembrete.message) && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {lembrete.data?.message || lembrete.message}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={() => {
              // O MESMO caminho do clique dentro do sininho: marca como lida e
              // usa `destinoDaNotificacao`. Dois caminhos para "abrir o evento"
              // divergiriam no dia em que a rota mudasse.
              handleNotificationClick(lembrete);
              closeToast();
            }}
          >
            Ver o evento
          </Button>
        </Box>
      ),
      {
        toastId: idDoToast,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        /*
         * Anotado QUANDO APARECE, e não quando é pedido.
         *
         * `onOpen` só dispara se o aviso de fato entrou na tela. Se o
         * container ainda não existir, o `react-toastify` descarta o pedido em
         * silêncio -- e aí isto não roda, o lembrete não é anotado, e a
         * próxima busca tenta de novo.
         *
         * `toast.isActive` logo após a chamada NÃO serve: ele responde falso
         * até o aviso ser desenhado, no tique seguinte. Medido na tela em
         * 14/10/2026 -- o aviso aparecia e não era anotado.
         */
        onOpen: () => anotarAvisosMostrados([lembrete.id]),
      }
    );

    return idDoToast;
  };

  /*
   * O lembrete da agenda vira AVISO NA TELA, preso até alguém fechar.
   *
   * Pedido de 14/10/2026. O lembrete já chegava -- `agenda:dispatch-reminders`
   * cria a notificação a cada minuto --, mas aparecia só aqui dentro do
   * sininho: quem não abrisse o sininho não ficava sabendo do compromisso, que
   * é o ponto de ter lembrete.
   *
   * Quem decide O QUE avisar é `utils/lembretesParaAvisar` -- regra testável
   * sem montar esta tela, e é lá que mora a janela de uma hora que impede uma
   * pilha de avisos presos para quem volta de férias.
   *
   * Mora aqui, e não numa tela, porque este componente vive no Template:
   * montado o tempo todo, em qualquer tela, e é ele que já busca.
   *
   * ## O atraso e a conferência não são zelo
   *
   * O `ToastContainer` é irmão deste componente na árvore do App, e na
   * PRIMEIRA busca -- que sai no `mount` -- ele ainda não existe. O
   * `react-toastify` descarta em silêncio o aviso levantado sem container.
   * Visto na tela em 14/10/2026: ao abrir a página o lembrete não aparecia, e
   * como já tinha sido anotado como avisado, não voltava nunca mais.
   *
   * Então o atraso dá tempo de o container montar, e quem anota é o `onOpen`
   * de cada aviso -- que só dispara quando ele entra na tela. O que não colou
   * fica sem anotar, e a próxima busca tenta de novo: o lembrete atrasa um
   * minuto em vez de se perder.
   */
  React.useEffect(() => {
    const aAvisar = lembretesParaAvisar(notifications, avisosJaMostrados());

    if (aAvisar.length === 0) return undefined;

    const relogio = setTimeout(() => aAvisar.forEach(avisarLembrete), 300);

    return () => clearTimeout(relogio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  /**
   * O aviso de chat encerrado PELO CLIENTE (ponto 6 da Gisele, 15/10/2026).
   *
   * A primeira versão levava o operador ao ticket automaticamente. Ele pediu
   * para trocar por um aviso no canto com um botão -- e está certo: quem
   * encerrou foi o cliente, a qualquer momento, e tirar o operador da tela em
   * que ele está é decidir por ele.
   *
   * Preso na tela, como o lembrete da agenda: sai no X ou no botão. Um aviso
   * que some sozinho enquanto o operador atende outra pessoa não avisa nada.
   *
   * O atraso de 300ms e o `onOpen` existem pelo mesmo motivo do lembrete --
   * ver a nota logo acima.
   */
  const avisarChatEncerrado = (aviso) => {
    toast.info(
      ({ closeToast }) => (
        <Box>
          {/*
            * `data` primeiro, igual à LISTA deste mesmo componente
            * (`notification.data?.title || notification.title`).
            *
            * Hoje os dois lugares dizem a mesma coisa -- `createSharedNotification`
            * grava o array inteiro em `data` ALÉM das colunas --, então isto
            * não muda nada na tela. É para que o toast e a lista não possam
            * discordar: eram duas leituras diferentes do mesmo campo, e a
            * primeira notificação que trouxesse o texto só num dos lados
            * apareceria inteira num e truncada no outro.
            */}
          <Typography variant="subtitle2" fontWeight={700}>
            {aviso.data?.title || aviso.title || 'Chat encerrado pelo cliente'}
          </Typography>
          {(aviso.data?.message || aviso.message) && (
            <Typography variant="body2" sx={{ mt: 0.25 }}>
              {aviso.data?.message || aviso.message}
            </Typography>
          )}
          <Button
            size="small"
            variant="outlined"
            sx={{ mt: 1, textTransform: 'none' }}
            onClick={() => {
              handleNotificationClick(aviso);
              closeToast();
            }}
          >
            {/*
              * O botão NOMEIA o chamado ("Abrir chamado #1234"), decidido por
              * ele em 02/11/2026. Dois motivos: o operador sabe aonde vai
              * antes de clicar, e reconhece a aba que vai aparecer no acesso
              * rápido -- é o mesmo número que a aba mostra. Sem número
              * (chamado do acervo antigo), volta a ser "Ir para o chamado".
              */}
            {rotuloParaAbrirOChamado(aviso)}
          </Button>
        </Box>
      ),
      {
        toastId: `chat-encerrado-${aviso.id}`,
        autoClose: false,
        closeOnClick: false,
        draggable: false,
        onOpen: () => anotarAvisosMostrados([aviso.id]),
      }
    );
  };

  React.useEffect(() => {
    const aAvisar = chatsEncerradosParaAvisar(notifications, avisosJaMostrados());

    if (aAvisar.length === 0) return undefined;

    const relogio = setTimeout(() => aAvisar.forEach(avisarChatEncerrado), 300);

    return () => clearTimeout(relogio);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [notifications]);

  // Filtrar notificações
  const filteredNotifications = notifications.filter(notification => {
    if (filter === 'all') return true;
    if (filter === 'unread') return !notification.is_read;
    return notification.data?.type === filter;
  });

  // Ordenar notificações
  const sortedNotifications = [...filteredNotifications].sort((a, b) => {
    if (sortBy === 'newest') {
      return new Date(b.created_at) - new Date(a.created_at);
    }
    return new Date(a.created_at) - new Date(b.created_at);
  });

  // Agrupa as notificações de ticket por ticket (uma linha por ticket com a
  // última atividade e a contagem de novidades).
  const ticketGroups = React.useMemo(() => {
    const map = new Map();
    sortedNotifications.forEach((n) => {
      const ticketId = n.data?.ticket_id;
      if (!ticketId) return;
      if (!map.has(ticketId)) {
        map.set(ticketId, {
          ticketId,
          title: n.data?.ticket_title || 'Ticket sem título',
          items: [],
        });
      }
      map.get(ticketId).items.push(n);
    });
    return Array.from(map.values())
      .map((g) => {
        const latest = [...g.items].sort((a, b) => new Date(b.created_at) - new Date(a.created_at))[0];
        return {
          ...g,
          count: g.items.length,
          unread: g.items.filter((i) => !i.is_read).length,
          latest,
        };
      })
      .sort((a, b) => new Date(b.latest.created_at) - new Date(a.latest.created_at));
  }, [sortedNotifications]);

  const handleTicketGroupClick = (group) => {
    group.items.forEach((i) => {
      if (!i.is_read) dispatch(markAsRead(i.id));
    });
    handleClose();
    history.push(`/ticket/${group.ticketId}`);
  };

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleNotificationClick = (notification) => {
    if (!notification.is_read) {
      dispatch(markAsRead(notification.id));
    }
    handleClose();

    // O destino mora em `utils/destinoDaNotificacao`, e nao aqui, por dois
    // motivos. Primeiro: era um `switch` com `default: break`, e esse
    // `break` era o defeito -- o lembrete da agenda e os avisos do mural
    // caiam nele e o clique nao fazia NADA, mesmo com a api mandando a rota.
    // Segundo: assim da para testar a regra sem montar esta tela.
    const destino = destinoDaNotificacao(notification);
    if (destino) {
      history.push(destino.rota, destino.estado);
    }
  };
  const handleMenuOpen = (event, notification) => {
    event.stopPropagation();
    setMenuAnchorEl(event.currentTarget);
    setSelectedNotification(notification);
  };

  const handleMenuClose = () => {
    setMenuAnchorEl(null);
    setSelectedNotification(null);
  };

  const handleMarkAsRead = (notificationId) => {
    dispatch(markAsRead(notificationId));
    handleMenuClose();
  };

  const handleDeleteNotification = (notificationId) => {
    dispatch(clearNotification(notificationId));
    handleMenuClose();
  };

  // Função para obter ícone baseado no tipo
  const getNotificationIcon = (type) => {
    const iconProps = { sx: { fontSize: 20 } };

    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return <AnnouncementIcon {...iconProps} color="primary" />;
      case NOTIFICATION_TYPES.COINS_EARNED:
        return <CoinsIcon {...iconProps} color="warning" />;
      case NOTIFICATION_TYPES.CELEBRATION:
        return <CelebrationIcon {...iconProps} color="secondary" />;
      case NOTIFICATION_TYPES.FEEDBACK:
        return <FeedbackIcon {...iconProps} color="info" />;
      case NOTIFICATION_TYPES.SCHEDULED_MEETING:
      case NOTIFICATION_TYPES.CALENDAR_REMINDER:
        return <ScheduleIcon {...iconProps} color="success" />;
      case NOTIFICATION_TYPES.COMMENT:
        return <CommentIcon {...iconProps} color="primary" />;
      case NOTIFICATION_TYPES.NEW_MESSAGE:
        return <MessageIcon {...iconProps} color="primary" />;
      case NOTIFICATION_TYPES.NEW_CLIENT:
        return <PersonIcon {...iconProps} color="success" />;
      case NOTIFICATION_TYPES.CHAT_CLOSED_BY_CLIENT:
        return <CheckCircleIcon {...iconProps} color="warning" />;
      default:
        return <NotificationsIcon {...iconProps} color="action" />;
    }
  };

  // Função para obter cor do chip baseado no tipo
  const getChipColor = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return 'primary';
      case NOTIFICATION_TYPES.COINS_EARNED:
        return 'warning';
      case NOTIFICATION_TYPES.CELEBRATION:
        return 'secondary';
      case NOTIFICATION_TYPES.FEEDBACK:
        return 'info';
      case NOTIFICATION_TYPES.SCHEDULED_MEETING:
      case NOTIFICATION_TYPES.CALENDAR_REMINDER:
        return 'success';
      case NOTIFICATION_TYPES.COMMENT:
        return 'primary';
      case NOTIFICATION_TYPES.NEW_MESSAGE:
        return 'info';
      case NOTIFICATION_TYPES.NEW_CLIENT:
        return 'success';
      case NOTIFICATION_TYPES.NEW_TICKET_ASSIGNED:
        return 'primary';
      case NOTIFICATION_TYPES.TICKET_UPDATED:
        return 'secondary';
      case NOTIFICATION_TYPES.CHAT_CLOSED_BY_CLIENT:
        return 'warning';
      default:
        return 'default';
    }
  };

  // Função para traduzir os tipos de notificação
  const getNotificationType = (type) => {
    switch (type) {
      case NOTIFICATION_TYPES.ANNOUNCEMENT:
        return 'Anúncio';
      case NOTIFICATION_TYPES.COINS_EARNED:
        return 'Moedas Ganhas';
      case NOTIFICATION_TYPES.CELEBRATION:
        return 'Celebração';
      case NOTIFICATION_TYPES.FEEDBACK:
        return 'Feedback';
      case NOTIFICATION_TYPES.SCHEDULED_MEETING:
        return 'Reunião Agendada';
      case NOTIFICATION_TYPES.CALENDAR_REMINDER:
        return 'Lembrete da Agenda';
      case NOTIFICATION_TYPES.COMMENT:
        return 'Comentário';
      case NOTIFICATION_TYPES.NEW_MESSAGE:
        return 'Nova Mensagem';
      case NOTIFICATION_TYPES.NEW_CLIENT:
        return 'Novo Cliente';
      case NOTIFICATION_TYPES.NEW_TICKET_ASSIGNED:
        return 'Novo Ticket Atribuído';
      case NOTIFICATION_TYPES.TICKET_UPDATED:
        return 'Ticket Atualizado';
      case NOTIFICATION_TYPES.CHAT_CLOSED_BY_CLIENT:
        return 'Chat Encerrado pelo Cliente';
      default:
        // `String(type)` porque `type` pode vir ausente: o componente le
        // `notification.data?.type || notification.type`, e `.replace`
        // em `undefined` derrubava a lista INTEIRA de notificacoes.
        return String(type || 'Notificação').replace(/_/g, ' ');
    }
  };

  const open = Boolean(anchorEl);

  return (
    <>
      <IconButton
        color="inherit"
        size="small"
        onClick={handleClick}
        sx={{ 
          position: 'relative',
          color: 'white',
          '&:hover': {
            backgroundColor: 'rgba(255, 255, 255, 0.1)',
          },
          '& svg': {
            color: 'white !important'
          }
        }}
      >
        <Badge badgeContent={unreadCount} color="error">
          <NotificationsIcon sx={{ color: 'white' }} />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            width: 450,
            maxHeight: 600,
            mt: 1
          }
        }}
      >
        {/* Header */}
        <Box sx={{ p: 2, borderBottom: 1, borderColor: 'divider' }}>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
            <Typography variant="h6" fontWeight="bold">
              Notificações
            </Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() => dispatch(markAllAsRead())}
              disabled={unreadCount === 0}
            >
              Marcar todas como lidas
            </Button>
          </Box>

          {/* Filtros */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Filtrar</InputLabel>
              <Select
                value={filter}
                label="Filtrar"
                onChange={(e) => setFilter(e.target.value)}
                size="small"
              >
                <MenuItem value="all">Todas</MenuItem>
                <MenuItem value="unread">Não lidas</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.ANNOUNCEMENT}>Comunicados</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.COINS_EARNED}>Moedas</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.CELEBRATION}>Celebrações</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.FEEDBACK}>Feedback</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.SCHEDULED_MEETING}>Reuniões</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.COMMENT}>Comentários</MenuItem>
                <MenuItem value={NOTIFICATION_TYPES.NEW_MESSAGE}>Mensagens</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ minWidth: 120 }}>
              <InputLabel>Ordenar</InputLabel>
              <Select
                value={sortBy}
                label="Ordenar"
                onChange={(e) => setSortBy(e.target.value)}
                size="small"
              >
                <MenuItem value="newest">Mais recentes</MenuItem>
                <MenuItem value="oldest">Mais antigas</MenuItem>
              </Select>
            </FormControl>

            <Chip
              label="Agrupar por ticket"
              size="small"
              color={groupByTicket ? 'primary' : 'default'}
              variant={groupByTicket ? 'filled' : 'outlined'}
              onClick={() => setGroupByTicket((v) => !v)}
              sx={{ alignSelf: 'center', cursor: 'pointer' }}
            />
          </Box>
        </Box>

        {/* Lista de Notificações */}
        {groupByTicket ? (
          ticketGroups.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
              <Typography variant="body2" color="text.secondary">
                Nenhuma notificação de ticket
              </Typography>
            </Box>
          ) : (
            <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
              {ticketGroups.map((group, index) => (
                <React.Fragment key={group.ticketId}>
                  <ListItem
                    sx={{
                      backgroundColor: group.unread > 0 ? 'action.hover' : 'transparent',
                      '&:hover': { backgroundColor: 'action.hover' },
                      cursor: 'pointer',
                      py: 1.5,
                      px: 2,
                    }}
                    onClick={() => handleTicketGroupClick(group)}
                  >
                    <ListItemAvatar>
                      <Avatar sx={{ bgcolor: group.unread > 0 ? 'primary.main' : 'grey.300', width: 40, height: 40 }}>
                        {getNotificationIcon(group.latest.data?.type || group.latest.type)}
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                          <Typography variant="body2" sx={{ fontWeight: group.unread > 0 ? 'bold' : 'normal' }} noWrap>
                            {group.title}
                          </Typography>
                          <Chip
                            label={`${group.count} novidade${group.count > 1 ? 's' : ''}`}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                          {group.unread > 0 && (
                            <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'primary.main', ml: 'auto' }} />
                          )}
                        </Box>
                      }
                      secondary={
                        <Typography variant="caption" color="text.secondary">
                          última: {getNotificationType(group.latest.data?.type || group.latest.type)} ·{' '}
                          {formatDistanceToNow(new Date(group.latest.created_at), { addSuffix: true, locale: ptBR })}
                        </Typography>
                      }
                    />
                  </ListItem>
                  {index < ticketGroups.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )
        ) : sortedNotifications.length === 0 ? (
          <Box sx={{ p: 4, textAlign: 'center' }}>
            <NotificationsIcon sx={{ fontSize: 48, color: 'grey.400', mb: 1 }} />
            <Typography variant="body2" color="text.secondary">
              Nenhuma notificação encontrada
            </Typography>
          </Box>
        ) : (
          <List sx={{ p: 0, maxHeight: 400, overflow: 'auto' }}>
            {sortedNotifications.map((notification, index) => (
              <React.Fragment key={notification.id}>
                <ListItem
                  sx={{
                    backgroundColor: notification.is_read ? 'transparent' : 'action.hover',
                    '&:hover': {
                      backgroundColor: 'action.hover',
                    },
                    cursor: 'pointer',
                    py: 1.5,
                    px: 2
                  }}
                  onClick={() => handleNotificationClick(notification)}
                >
                  <ListItemAvatar>
                    <Avatar
                      sx={{
                        bgcolor: notification.is_read ? 'grey.300' : 'primary.main',
                        width: 40,
                        height: 40
                      }}
                    >
                      {getNotificationIcon(notification.data?.type || notification.type)}
                    </Avatar>
                  </ListItemAvatar>

                  <ListItemText
                    primary={
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                        <Typography
                          variant="body2"
                          sx={{
                            fontWeight: notification.is_read ? 'normal' : 'bold',
                            color: notification.is_read ? 'text.secondary' : 'text.primary'
                          }}
                        >
                          {notification.data?.title || notification.title}
                        </Typography>
                        <Chip
                          label={getNotificationType(notification.data?.type || notification.type)}
                          size="small"
                          color={getChipColor(notification.data?.type || notification.type)}
                          variant="outlined"
                        />
                        {!notification.is_read && (
                          <Box
                            sx={{
                              width: 6,
                              height: 6,
                              borderRadius: '50%',
                              bgcolor: 'primary.main',
                              ml: 1
                            }}
                          />
                        )}
                      </Box>
                    }
                    secondary={
                      <Box>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          sx={{
                            display: '-webkit-box',
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: 'vertical',
                            overflow: 'hidden',
                            mb: 0.5
                          }}
                        >
                          {notification.data?.message || notification.message}
                        </Typography>
                        <Typography
                          variant="caption"
                          color="text.disabled"
                        >
                          {formatDistanceToNow(new Date(notification.created_at), {
                            addSuffix: true,
                            locale: ptBR
                          })}
                        </Typography>
                      </Box>
                    }
                  />

                  <IconButton
                    onClick={(e) => handleMenuOpen(e, notification)}
                    size="small"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </ListItem>
                {index < sortedNotifications.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}

        {/* Menu de Opções */}
        <Menu
          anchorEl={menuAnchorEl}
          open={Boolean(menuAnchorEl)}
          onClose={handleMenuClose}
        >
          {selectedNotification && !selectedNotification.is_read && (
            <MenuItem onClick={() => handleMarkAsRead(selectedNotification.id)}>
              <CheckCircleIcon sx={{ mr: 1, fontSize: 16 }} />
              Marcar como lida
            </MenuItem>
          )}
          <MenuItem onClick={() => handleDeleteNotification(selectedNotification?.id)}>
            <ClearIcon sx={{ mr: 1, fontSize: 16 }} />
            Excluir
          </MenuItem>
        </Menu>
      </Popover>
    </>
  );
};

export default NotificationBadge;
